import { RewardToastSystem } from '@/services/reward-toast-service'
import { useUserStore } from '@/stores/user'
import type { RouteLocation, Router } from 'vue-router'

import { getPreviousRoute, setPreviousRoute } from '../historyTracker'

/**
 * Handles URL parameters that trigger specific actions.
 * This function runs after each route navigation to check for special parameters
 * and perform appropriate actions (like showing reward-claim toasts for guests).
 *
 * @param {RouteLocation} to
 */
export async function handleUrlParameters(
  to: RouteLocation,
  router: Router
): Promise<void> {
  // Check for 'res-claimed' URL parameter and show toast notification
  return await checkForResClaimedParam(to, router)
}

/**
 * Checks for the 'res-claimed' URL parameter and shows a toast notification if found.
 * Only shows the toast for logged-out users, as logged-in users receive real-time
 * notifications via the broadcast system.
 * Only shows the toast if the parameter value is a valid number greater than 0.
 * Logged-out users see a generic reward reminder prompting them to create an account.
 * Uses router.replace() to update both URL and router state synchronously, while
 * preserving the correct previous route tracking by restoring it after the replace.
 * Preserves hash fragments and handles null/undefined array values correctly.
 *
 * @param {RouteLocation} to
 */
async function checkForResClaimedParam(
  to: RouteLocation,
  router: Router
): Promise<void> {
  // Check if 'res-claimed' parameter exists in the query
  if (to.query && 'res-claimed' in to.query) {
    const resClaimedValue = to.query['res-claimed']

    // Parse the value as a number so we only honour positive numeric flags
    const creditsAmount =
      typeof resClaimedValue === 'string'
        ? parseFloat(resClaimedValue)
        : Array.isArray(resClaimedValue)
          ? parseFloat(resClaimedValue[0] || '0')
          : 0

    // Only proceed if we have a valid number greater than 0
    if (!isNaN(creditsAmount) && creditsAmount > 0) {
      // For logged-in users, skip the URL parameter toast as they get real-time notifications via broadcast
      const userStore = useUserStore()
      if (!userStore.isLoggedIn) {
        await RewardToastSystem.showCreditToast(
          creditsAmount,
          true,
          "You've unlocked a reward! Sign up to access it.",
          '/?register'
        )
      }

      // Clean up the URL by removing the parameter
      const newQuery = { ...to.query }
      delete newQuery['res-claimed']

      // Store the current previous route before router.replace overwrites it
      const realPreviousRoute = getPreviousRoute()

      // Use router.replace to update both URL and router state synchronously
      // This ensures URL and router.currentRoute are synchronized
      await router
        .replace({
          name: to.name as string,
          query: newQuery,
          hash: to.hash,
          params: to.params,
          replace: true,
        })
        .then(() => {
          // Restore the correct previous route after the replace
          if (realPreviousRoute) {
            setPreviousRoute(realPreviousRoute)
          }
        })
    }
  }
}
